/**
 * Standard Parts Catalog — parametric fastener library.
 * Each part is a script template function that generates valid replicad JavaScript.
 * Standard parts are inserted as ImportedPartFeature with importDocId "std:<partId>-<size>".
 */

export interface StandardPartSize {
  label: string;
  params: Record<string, number>;
}

export interface StandardPartDefinition {
  id: string;
  name: string;
  category: "fastener" | "bearing";
  sizes: StandardPartSize[];
  defaultSizeIndex: number;
  /** Fields exposed as user-adjustable beyond size selection */
  extraFields?: {
    key: string;
    label: string;
    min?: number;
    max?: number;
    step?: number;
    default: number | string;
    options?: string[];
  }[];
  scriptTemplate: (params: Record<string, any>) => string;
}

// ---------------------------------------------------------------------------
// ISO Metric Hex Bolt (DIN 933 / ISO 4017)
// ---------------------------------------------------------------------------

const HEX_BOLT_SIZES: StandardPartSize[] = [
  { label: "M3",  params: { d: 3,  af: 5.5,  hh: 2,    len: 10 } },
  { label: "M4",  params: { d: 4,  af: 7,    hh: 2.8,  len: 12 } },
  { label: "M5",  params: { d: 5,  af: 8,    hh: 3.5,  len: 16 } },
  { label: "M6",  params: { d: 6,  af: 10,   hh: 4,    len: 20 } },
  { label: "M8",  params: { d: 8,  af: 13,   hh: 5.3,  len: 25 } },
  { label: "M10", params: { d: 10, af: 16,   hh: 6.4,  len: 30 } },
  { label: "M12", params: { d: 12, af: 18,   hh: 7.5,  len: 35 } },
];

function hexBoltScript(p: Record<string, number>): string {
  const r = (p.af / 2) / Math.cos(Math.PI / 6); // circumradius from across-flats
  const pts = Array.from({ length: 6 }, (_, i) => {
    const a = (i * 60) * Math.PI / 180;
    return `[${(r * Math.cos(a)).toFixed(4)}, ${(r * Math.sin(a)).toFixed(4)}]`;
  });
  return `const { Sketcher, makeCylinder } = replicad;

/** Thread: ${p.thread || "coarse"} */
function main() {
  const head = new Sketcher("XY")
    .movePointerTo(${pts[0]})
    .lineTo(${pts[1]})
    .lineTo(${pts[2]})
    .lineTo(${pts[3]})
    .lineTo(${pts[4]})
    .lineTo(${pts[5]})
    .close()
    .extrude(${p.hh});
  const shank = makeCylinder(${p.d / 2}, ${p.len}).translateZ(-${p.len});
  return head.fuse(shank);
}`;
}

// ---------------------------------------------------------------------------
// ISO Metric Hex Nut (DIN 934 / ISO 4032)
// ---------------------------------------------------------------------------

const HEX_NUT_SIZES: StandardPartSize[] = [
  { label: "M3",  params: { d: 3,  af: 5.5,  h: 2.4  } },
  { label: "M4",  params: { d: 4,  af: 7,    h: 3.2  } },
  { label: "M5",  params: { d: 5,  af: 8,    h: 4.7  } },
  { label: "M6",  params: { d: 6,  af: 10,   h: 5.2  } },
  { label: "M8",  params: { d: 8,  af: 13,   h: 6.8  } },
  { label: "M10", params: { d: 10, af: 16,   h: 8.4  } },
  { label: "M12", params: { d: 12, af: 18,   h: 10.8 } },
];

function hexNutScript(p: Record<string, number>): string {
  const r = (p.af / 2) / Math.cos(Math.PI / 6);
  const pts = Array.from({ length: 6 }, (_, i) => {
    const a = (i * 60) * Math.PI / 180;
    return `[${(r * Math.cos(a)).toFixed(4)}, ${(r * Math.sin(a)).toFixed(4)}]`;
  });
  return `const { Sketcher, makeCylinder } = replicad;

/** Thread: ${p.thread || "coarse"} */
function main() {
  const outer = new Sketcher("XY")
    .movePointerTo(${pts[0]})
    .lineTo(${pts[1]})
    .lineTo(${pts[2]})
    .lineTo(${pts[3]})
    .lineTo(${pts[4]})
    .lineTo(${pts[5]})
    .close()
    .extrude(${p.h});
  const hole = makeCylinder(${p.d / 2}, ${p.h + 1}).translateZ(-0.5);
  return outer.cut(hole);
}`;
}

// ---------------------------------------------------------------------------
// ISO Flat Washer (DIN 125 / ISO 7089)
// ---------------------------------------------------------------------------

const FLAT_WASHER_SIZES: StandardPartSize[] = [
  { label: "M3",  params: { di: 3.2,  d_out: 7,   t: 0.5 } },
  { label: "M4",  params: { di: 4.3,  d_out: 9,   t: 0.8 } },
  { label: "M5",  params: { di: 5.3,  d_out: 10,  t: 1   } },
  { label: "M6",  params: { di: 6.4,  d_out: 12,  t: 1.6 } },
  { label: "M8",  params: { di: 8.4,  d_out: 16,  t: 1.6 } },
  { label: "M10", params: { di: 10.5, d_out: 20,  t: 2   } },
  { label: "M12", params: { di: 13,   d_out: 24,  t: 2.5 } },
];

function flatWasherScript(p: Record<string, number>): string {
  return `const { makeCylinder } = replicad;

function main() {
  const outer = makeCylinder(${p.d_out / 2}, ${p.t});
  const hole = makeCylinder(${p.di / 2}, ${p.t + 1}).translateZ(-0.5);
  return outer.cut(hole);
}`;
}

// ---------------------------------------------------------------------------
// ISO Socket Head Cap Screw (DIN 912 / ISO 4762)
// ---------------------------------------------------------------------------

const SHCS_SIZES: StandardPartSize[] = [
  { label: "M3",  params: { d: 3,  hd: 5.5,  hh: 3,    len: 10 } },
  { label: "M4",  params: { d: 4,  hd: 7,    hh: 4,    len: 12 } },
  { label: "M5",  params: { d: 5,  hd: 8.5,  hh: 5,    len: 16 } },
  { label: "M6",  params: { d: 6,  hd: 10,   hh: 6,    len: 20 } },
  { label: "M8",  params: { d: 8,  hd: 13,   hh: 8,    len: 25 } },
  { label: "M10", params: { d: 10, hd: 16,   hh: 10,   len: 30 } },
  { label: "M12", params: { d: 12, hd: 18,   hh: 12,   len: 35 } },
];

function shcsScript(p: Record<string, number>): string {
  return `const { makeCylinder } = replicad;

function main() {
  const head = makeCylinder(${p.hd / 2}, ${p.hh});
  const shank = makeCylinder(${p.d / 2}, ${p.len}).translateZ(-${p.len});
  return head.fuse(shank);
}`;
}

// ---------------------------------------------------------------------------
// ISO Deep Groove Ball Bearing (6200 series / ISO 15)
// ---------------------------------------------------------------------------

const BALL_BEARING_SIZES: StandardPartSize[] = [
  { label: "6200", params: { d: 10, D: 30, B: 9   } },
  { label: "6201", params: { d: 12, D: 32, B: 10  } },
  { label: "6202", params: { d: 15, D: 35, B: 11  } },
  { label: "6203", params: { d: 17, D: 40, B: 12  } },
  { label: "6204", params: { d: 20, D: 47, B: 14  } },
  { label: "6205", params: { d: 25, D: 52, B: 15  } },
  { label: "6206", params: { d: 30, D: 62, B: 16  } },
  { label: "6208", params: { d: 40, D: 80, B: 18  } },
  { label: "6210", params: { d: 50, D: 90, B: 20  } },
  { label: "6212", params: { d: 60, D: 110, B: 22 } },
];

function ballBearingScript(p: Record<string, number>): string {
  const ri = p.d / 2;   // inner radius
  const ro = p.D / 2;   // outer radius
  const rm = (ri + ro) / 2; // midpoint (race center)
  const rw = (ro - ri) / 2 * 0.4; // ring wall thickness
  return `const { makeCylinder } = replicad;

function main() {
  const outer = makeCylinder(${ro}, ${p.B}).translateZ(-${p.B / 2});
  const outerHole = makeCylinder(${rm + rw}, ${p.B + 1}).translateZ(-${p.B / 2 + 0.5});
  const outerRing = outer.cut(outerHole);
  const inner = makeCylinder(${rm - rw}, ${p.B}).translateZ(-${p.B / 2});
  const innerHole = makeCylinder(${ri}, ${p.B + 1}).translateZ(-${p.B / 2 + 0.5});
  const innerRing = inner.cut(innerHole);
  return outerRing.fuse(innerRing);
}`;
}

// ---------------------------------------------------------------------------
// ISO Cylindrical Roller Bearing (NU series / ISO 15)
// ---------------------------------------------------------------------------

const ROLLER_BEARING_SIZES: StandardPartSize[] = [
  { label: "NU204", params: { d: 20, D: 47, B: 14  } },
  { label: "NU205", params: { d: 25, D: 52, B: 15  } },
  { label: "NU206", params: { d: 30, D: 62, B: 16  } },
  { label: "NU208", params: { d: 40, D: 80, B: 18  } },
  { label: "NU210", params: { d: 50, D: 90, B: 20  } },
  { label: "NU212", params: { d: 60, D: 110, B: 22 } },
];

function rollerBearingScript(p: Record<string, number>): string {
  const ri = p.d / 2;
  const ro = p.D / 2;
  const rm = (ri + ro) / 2;
  const rw = (ro - ri) / 2 * 0.35;
  return `const { makeCylinder } = replicad;

function main() {
  const outer = makeCylinder(${ro}, ${p.B}).translateZ(-${p.B / 2});
  const outerHole = makeCylinder(${rm + rw}, ${p.B + 1}).translateZ(-${p.B / 2 + 0.5});
  const outerRing = outer.cut(outerHole);
  const inner = makeCylinder(${rm - rw}, ${p.B}).translateZ(-${p.B / 2});
  const innerHole = makeCylinder(${ri}, ${p.B + 1}).translateZ(-${p.B / 2 + 0.5});
  const innerRing = inner.cut(innerHole);
  return outerRing.fuse(innerRing);
}`;
}

// ---------------------------------------------------------------------------
// ISO Thrust Ball Bearing (51200 series / ISO 104)
// ---------------------------------------------------------------------------

const THRUST_BEARING_SIZES: StandardPartSize[] = [
  { label: "51200", params: { d: 10, D: 26, B: 11  } },
  { label: "51201", params: { d: 12, D: 28, B: 11  } },
  { label: "51202", params: { d: 15, D: 32, B: 12  } },
  { label: "51204", params: { d: 20, D: 40, B: 14  } },
  { label: "51205", params: { d: 25, D: 47, B: 15  } },
  { label: "51206", params: { d: 30, D: 52, B: 16  } },
  { label: "51208", params: { d: 40, D: 68, B: 19  } },
  { label: "51210", params: { d: 50, D: 78, B: 22  } },
  { label: "51212", params: { d: 60, D: 95, B: 26  } },
];

function thrustBearingScript(p: Record<string, number>): string {
  const ri = p.d / 2;
  const ro = p.D / 2;
  const washerH = p.B * 0.35; // each washer is ~35% of total height
  return `const { makeCylinder } = replicad;

function main() {
  const topWasher = makeCylinder(${ro}, ${washerH.toFixed(1)});
  const topHole = makeCylinder(${ri}, ${washerH + 1}).translateZ(-0.5);
  const top = topWasher.cut(topHole);
  const bottomWasher = makeCylinder(${ro}, ${washerH.toFixed(1)}).translateZ(-${(p.B - washerH).toFixed(1)});
  const bottomHole = makeCylinder(${ri}, ${washerH + 1}).translateZ(-${(p.B - washerH + 0.5).toFixed(1)});
  const bottom = bottomWasher.cut(bottomHole);
  return top.fuse(bottom);
}`;
}

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export const STANDARD_PARTS_CATALOG: StandardPartDefinition[] = [
  {
    id: "hex-bolt",
    name: "Hex Bolt",
    category: "fastener",
    sizes: HEX_BOLT_SIZES,
    defaultSizeIndex: 3, // M6
    extraFields: [
      { key: "len", label: "Length", min: 5, max: 100, step: 1, default: 20 },
      { key: "thread", label: "Thread", options: ["coarse", "fine"], default: "coarse" },
    ],
    scriptTemplate: hexBoltScript,
  },
  {
    id: "hex-nut",
    name: "Hex Nut",
    category: "fastener",
    sizes: HEX_NUT_SIZES,
    defaultSizeIndex: 3,
    extraFields: [
      { key: "thread", label: "Thread", options: ["coarse", "fine"], default: "coarse" },
    ],
    scriptTemplate: hexNutScript,
  },
  {
    id: "flat-washer",
    name: "Flat Washer",
    category: "fastener",
    sizes: FLAT_WASHER_SIZES,
    defaultSizeIndex: 3,
    scriptTemplate: flatWasherScript,
  },
  {
    id: "socket-head-cap-screw",
    name: "Socket Head Cap Screw",
    category: "fastener",
    sizes: SHCS_SIZES,
    defaultSizeIndex: 3,
    extraFields: [{ key: "len", label: "Length", min: 5, max: 100, step: 1, default: 20 }],
    scriptTemplate: shcsScript,
  },
  // Bearings
  {
    id: "ball-bearing",
    name: "Ball Bearing",
    category: "bearing",
    sizes: BALL_BEARING_SIZES,
    defaultSizeIndex: 6, // 6206
    scriptTemplate: ballBearingScript,
  },
  {
    id: "roller-bearing",
    name: "Roller Bearing",
    category: "bearing",
    sizes: ROLLER_BEARING_SIZES,
    defaultSizeIndex: 2, // NU206
    scriptTemplate: rollerBearingScript,
  },
  {
    id: "thrust-bearing",
    name: "Thrust Bearing",
    category: "bearing",
    sizes: THRUST_BEARING_SIZES,
    defaultSizeIndex: 5, // 51206
    scriptTemplate: thrustBearingScript,
  },
];

/** Generate the import doc ID for a standard part + size. */
export function stdPartDocId(partId: string, sizeLabel: string): string {
  return `std:${partId}-${sizeLabel.toLowerCase()}`;
}

/** Generate a display name for a standard part + size + extra params. */
export function stdPartName(partDef: StandardPartDefinition, size: StandardPartSize, extras?: Record<string, number | string>): string {
  const lengthSuffix = extras?.len ? `x${extras.len}` : "";
  return `${partDef.name} ${size.label}${lengthSuffix}`;
}

/** Check if a doc ID is a standard part. */
export function isStandardPart(docId: string): boolean {
  return docId.startsWith("std:");
}
